import React from "react";
import { Info } from "lucide-react";

import { selectInsightStatements } from "./performanceSelectors";

/**
 * Two sentences a reader needs BEFORE reading any figure on this tab.
 *
 * Both come from `data.methodology` — the backend's own words, not a paraphrase.
 * The selector takes the first paragraph of the revenue section (how a part
 * month is prorated) and of the result section (that this is not a company
 * profit), because those are the two assumptions under which every number here
 * is true, and a reader who misses the second one will read "Objektergebnis" as
 * "Gewinn".
 *
 * WHY NOT THE WHOLE METHODOLOGY. It is five sections long. A banner carrying all
 * of it is a banner nobody reads, which is worse than none — the full text stays
 * one click away in the popover on the Objektergebnis card.
 *
 * WHY NOTHING IS HARDCODED. If the wire carries no methodology, this renders
 * nothing. A fallback sentence written here would be a second copy of a business
 * statement, free to keep describing a computation the backend has changed —
 * and it would be least visible exactly when the wire is broken, which is when
 * being wrong costs most.
 */
export default function PerformanceInsightBanner({ methodology }) {
  const statements = selectInsightStatements(methodology);
  if (!statements.length) return null;

  return (
    <section
      data-testid="performance-insight-banner"
      aria-labelledby="performance-insight-heading"
      className="rounded-xl border border-border bg-muted/40 p-4"
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
        >
          <Info className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
          <h2
            id="performance-insight-heading"
            className="m-0 text-[12px] font-semibold text-foreground"
          >
            Wie diese Zahlen zu lesen sind
          </h2>
          <ul className="mt-1.5 list-none space-y-1 p-0">
            {statements.map((statement) => (
              <li
                key={statement.text}
                className="text-[11px] leading-[1.5] text-muted-foreground"
              >
                {statement.text}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
